import { useEffect, useState } from 'react';
import { Input } from 'antd';

import { CurrencyControl } from '../Input';
import { isCurrencyValue, parseCurrency } from '../../utils/currency';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

const TEMPLATE_TOKEN_RE = /\{\{/;

/**
 * The value input for a currency CONDITION, FILTER or PREFILL: the same
 * control the item form uses (amount + the element's currency), over a raw
 * value that may be `{ amount, currency }`, a bare number (major units in
 * the element's default currency) or a string literal ("20.00 USD", "$5").
 * The measurement twin is MeasurementValueInput; this follows it exactly.
 *
 * A string that does not read — a hand-typed query with a typo, a currency
 * the element does not name — is shown AS TYPED in a plain text box so it
 * can be fixed, rather than opening an empty control that would drop it on
 * the first keystroke. Text mode is sticky for the life of this input.
 * `onChange` receives `{ amount, currency }` / null from the control, or the
 * raw text from the text box. A `{{...}}` template token sits in the text
 * box without the "unreadable" warning (the API substitutes it).
 */
export default function CurrencyValueInput({
	raw,
	options,
	name,
	label,
	id,
	'aria-label': ariaLabel,
	className,
	disabled = false,
	size = 'middle',
	onChange,
}) {
	// Read the way the API compiler reads a literal: the element's
	// currencies decide what "$" means, and a bound may be finer than a cent.
	const entry = isCurrencyValue(raw) ? raw : parseCurrency(raw, { ...(options || {}), fraction: 'round' });
	const hasText = raw !== undefined && raw !== null && raw !== '';
	const unreadable = hasText && !entry;
	const [textMode, setTextMode] = useState(unreadable);

	useEffect(() => {
		if (unreadable) setTextMode(true);
	}, [unreadable]);

	if (textMode || unreadable) {
		const text = typeof raw === 'string' ? raw : String(raw ?? '');
		const flagged = unreadable && !TEMPLATE_TOKEN_RE.test(text);
		return (
			<Input
				{...NO_TEXT_ASSIST}
				id={id}
				className={className}
				status={flagged ? 'warning' : undefined}
				size={size}
				disabled={disabled}
				aria-label={ariaLabel}
				value={text}
				placeholder='e.g. 20.00 USD'
				title={flagged ? 'Not a readable amount for this element' : undefined}
				onChange={event => onChange(event.target.value)}
			/>
		);
	}

	return (
		<CurrencyControl
			id={id}
			size={size}
			value={entry || null}
			element={{ name, label: { singular: label || name }, options: options || {} }}
			disabled={disabled}
			aria-label={ariaLabel}
			onChange={onChange}
		/>
	);
}
